// DRONES : les FORMATIONS, calculées dans un Web Worker (R17).
//
// Tout ce fichier est AUTONOME : aucune importation, aucune référence
// extérieure, rien de plus récent qu'ES2017. Le worker est construit en texte
// à partir des sources des fonctions (Function.prototype.toString), et les
// mêmes fonctions tournent sous Node dans les tests (comme lightpaint-contours).
//
// formation(kind, o) rend, pour N drones :
//   tex     Float32Array N x 4 - ce que lit le vertex shader : selon le genre,
//           une position à l'écran ou des coordonnées locales (voir KINDS), et
//           la couleur 8 bits par canal empaquetée dans le 4e flottant
//           (r x 65536 + g x 256 + b, exact sur les 24 bits d'un float32)
//   screen  Float32Array N x 2 - la position de repos à l'écran (unités
//           « s » : x = ndc.x x aspect, y = ndc.y), pour l'assignation et les tests
//   par     [4] les paramètres du genre (centres, rayon, amplitude...)
// Les points sont ensuite ASSIGNÉS aux drones (assign) : rangs le long d'une
// courbe de Hilbert, les positions actuelles des drones contre les cibles -
// des drones voisins vont vers des cibles voisines, les arcs ne se croisent
// presque pas (une assignation au hasard fait un enchevêtrement).
//
// Les EMPLACEMENTS (layout) : le ciel moins la pochette DOM et les zones
// d'interface. top = la bande au-dessus de la pochette ; left / right = les
// bandes de part et d'autre. Chaque forme y est RECADRÉE (R6) : mise à
// l'échelle pour occuper sa place, proportions gardées.

export const KINDS = { cover: 0, title: 0, sphere: 1, wave: 2, heart: 3, spiral: 4, bpm: 5 };
export const FORMS = ['sphere', 'title', 'wave', 'cover', 'spiral', 'heart', 'bpm'];

export function packColour(r, g, b) {
  const q = (v) => Math.max(0, Math.min(255, Math.round(v * 255)));
  return q(r) * 65536 + q(g) * 256 + q(b);
}

export function hilbertD(n, x, y) {
  let d = 0;
  for (let s = n >> 1; s > 0; s >>= 1) {
    const rx = (x & s) > 0 ? 1 : 0, ry = (y & s) > 0 ? 1 : 0;
    d += s * s * ((3 * rx) ^ ry);
    if (ry === 0) { if (rx === 1) { x = s - 1 - x; y = s - 1 - y; } const t = x; x = y; y = t; }
  }
  return d;
}

// les emplacements, d'après l'écran et le rectangle de la pochette (unités s)
// `cover` est la pochette RÉELLE (sur demande, 15/10/2026 : le trou dans les
// formations n'existe que quand la pochette est au centre). Au centre : le
// haut, la gauche et la droite autour d'elle. Ailleurs (un coin) ou absente
// (cover.none) : le milieu du ciel, coupé seulement du côté de la pochette
// (en largeur ou en hauteur, ce qui garde le plus de ciel), sans trou.
export function computeLayout(aspect, cover) {
  const xEdge = 0.92 * aspect;
  const yTop = 0.74, yBot = -0.52;              // sous la bande des boutons (12 %), et le bas du cadre
  const centred = !cover.none && Math.abs(cover.cx) < 0.25 && Math.abs(cover.cy) < 0.3;
  if (centred) {
    const coverTop = cover.cy + cover.hh, coverL = cover.cx - cover.hw, coverR = cover.cx + cover.hw;
    return {
      aspect, cover, yTop, yBot, centred,
      top: { x0: -xEdge, x1: xEdge, y0: Math.min(coverTop + 0.07, yTop - 0.12), y1: yTop },
      left: { x0: -xEdge, x1: coverL - 0.09, y0: yBot, y1: yTop },
      right: { x0: coverR + 0.09, x1: xEdge, y0: yBot, y1: yTop }
    };
  }
  let x0 = -xEdge, x1 = xEdge, y0 = yBot, y1 = yTop;
  if (!cover.none && cover.cy - cover.hh < yTop + 0.07 && cover.cy + cover.hh > yBot - 0.07) {
    // trim the side (in x) or the band (in y) the cover sits in, whichever keeps more sky
    const ax = cover.cx > 0 ? [x0, Math.min(x1, cover.cx - cover.hw - 0.09)] : [Math.max(x0, cover.cx + cover.hw + 0.09), x1];
    const ay = cover.cy > 0 ? [y0, Math.min(y1, cover.cy - cover.hh - 0.07)] : [Math.max(y0, cover.cy + cover.hh + 0.07), y1];
    if ((ax[1] - ax[0]) * (y1 - y0) >= (x1 - x0) * (ay[1] - ay[0])) { x0 = ax[0]; x1 = ax[1]; } else { y0 = ay[0]; y1 = ay[1]; }
  }
  const mid = (x0 + x1) / 2;
  return {
    aspect, cover, yTop: y1, yBot: y0, centred: false,
    top: { x0, x1, y0: y1 - (y1 - y0) * 0.45, y1 },
    left: { x0, x1: mid - 0.03, y0, y1 },
    right: { x0: mid + 0.03, x1, y0, y1 }
  };
}

function fit(slot, w, h, fill) {
  const sw = slot.x1 - slot.x0, sh = slot.y1 - slot.y0;
  const k = Math.min(sw / w, sh / h) * (fill || 0.92);
  return { k, cx: (slot.x0 + slot.x1) / 2, cy: (slot.y0 + slot.y1) / 2 };
}

function rngOf(seed) { let s = seed >>> 0; return function () { s = (s * 1664525 + 1013904223) >>> 0; return s / 4294967296; }; }

// des points sur l'encre d'un raster (alpha > 127), exactement n, grille
// légèrement dispersée (comme le titre en étincelles de Shapes) ; rend des
// coordonnées locales centrées (y vers le haut) et la boîte de l'encre
function sampleInk(raster, n, seed) {
  const w = raster.w, h = raster.h, img = raster.data;
  let x0 = w, x1 = 0, y0 = h, y1 = 0, filled = 0;
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) if (img[(y * w + x) * 4 + 3] > 127) { filled++; if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
  if (!filled || n <= 0) return null;
  const rnd = rngOf(seed || 7);
  let step = Math.sqrt(filled / n) * 1.05, pts = [];
  for (let tries = 0; tries < 14; tries++) {
    pts = [];
    for (let gy = y0; gy <= y1; gy += step) for (let gx = x0; gx <= x1; gx += step) {
      const x = Math.floor(gx + rnd() * step * 0.6), y = Math.floor(gy + rnd() * step * 0.6);
      if (x > x1 || y > y1) continue;
      if (img[(y * w + x) * 4 + 3] > 127) pts.push(x, y);
    }
    if (pts.length / 2 >= n) break;
    step *= 0.9;
  }
  const have = pts.length / 2, out = new Float32Array(n * 2);
  const cx = (x0 + x1) / 2, cy = (y0 + y1) / 2;
  for (let i = 0; i < n; i++) {
    const j = Math.min(have - 1, Math.floor(i * have / n));
    out[i * 2] = pts[j * 2] - cx; out[i * 2 + 1] = -(pts[j * 2 + 1] - cy);
  }
  return { pts: out, w: x1 - x0 + 1, h: y1 - y0 + 1 };
}

// la couleur d'un drone hors pochette : un dégradé de palette le long de x
function paletteAt(pal, t) {
  const n = pal.length;
  if (n === 1) return pal[0];
  const u = Math.max(0, Math.min(0.9999, t)) * (n - 1), i = Math.floor(u), f = u - i;
  const a = pal[i], b = pal[Math.min(n - 1, i + 1)];
  return [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f, a[2] + (b[2] - a[2]) * f];
}

// o : { N, layout, palette: [[r,g,b] 0..1], seed, side ('left'|'right'),
//       cover: { w, h, data (RGBA, 2x la grille) } | null, grid: [gw, gh],
//       text: { w, h, data } (titre ou chiffres du tempo) }
export function formation(kind, o) {
  const N = o.N, L = o.layout, pal = o.palette && o.palette.length ? o.palette : [[1, 0.3, 0.7], [0.3, 0.7, 1]];
  const tex = new Float32Array(N * 4), screen = new Float32Array(N * 2);
  let par = [0, 0, 0, 0];
  const rnd = rngOf(o.seed || 11);
  const set = (i, tx, ty, tz, col, sx, sy) => { tex[i * 4] = tx; tex[i * 4 + 1] = ty; tex[i * 4 + 2] = tz; tex[i * 4 + 3] = packColour(col[0], col[1], col[2]); screen[i * 2] = sx; screen[i * 2 + 1] = sy; };
  const xspan = L.aspect * 1.84;
  const palX = (sx) => paletteAt(pal, (sx + L.aspect * 0.92) / xspan);
  const half = Math.floor(N / 2);

  if (kind === 'cover') {
    // the cover not in the centre: the image takes the whole middle of the sky
    const side = L.centred === false ? { x0: L.left.x0, x1: L.right.x1, y0: L.yBot, y1: L.yTop } : (o.side === 'right' ? L.right : L.left);
    const gw = o.grid[0], gh = o.grid[1];
    const f = fit(side, gw, gh, 0.86);
    const sp = f.k;                                 // l'écart entre drones (unités s)
    let i = 0;
    for (let gy = 0; gy < gh && i < N; gy++) for (let gx = 0; gx < gw && i < N; gx++, i++) {
      const sx = f.cx + (gx - (gw - 1) / 2) * sp, sy = f.cy - (gy - (gh - 1) / 2) * sp;
      let col;
      if (o.cover) {
        // la moyenne du bloc 2 x 2 de l'image (décodée à 2 x la grille)
        const W = o.cover.w, d = o.cover.data; let r = 0, g = 0, b = 0;
        for (let dy = 0; dy < 2; dy++) for (let dx = 0; dx < 2; dx++) { const p = ((gy * 2 + dy) * W + gx * 2 + dx) * 4; r += d[p]; g += d[p + 1]; b += d[p + 2]; }
        col = [r / 1020, g / 1020, b / 1020];
      } else {
        // repli sans pochette (R17) : des anneaux concentriques aux couleurs de la palette
        const rr = Math.hypot((gx - (gw - 1) / 2) / (gw / 2), (gy - (gh - 1) / 2) / (gh / 2));
        col = paletteAt(pal, (Math.sin(rr * 9) * 0.5 + 0.5));
      }
      set(i, sx, sy, 0, col, sx, sy);
    }
    // les drones en trop : un cadre pointillé autour de l'image
    const rest = N - i;
    if (rest > 0) {
      const wv = gw * sp + 2 * sp, hv = gh * sp + 2 * sp, per = 2 * (wv + hv);
      for (let k = 0; k < rest; k++, i++) {
        let u = (k + 0.5) / rest * per, sx, sy;
        if (u < wv) { sx = f.cx - wv / 2 + u; sy = f.cy + hv / 2; } else if ((u -= wv) < hv) { sx = f.cx + wv / 2; sy = f.cy + hv / 2 - u; } else if ((u -= hv) < wv) { sx = f.cx + wv / 2 - u; sy = f.cy - hv / 2; } else { u -= wv; sx = f.cx - wv / 2; sy = f.cy - hv / 2 + u; }
        set(i, sx, sy, 0, [0.85, 0.85, 0.9], sx, sy);
      }
    }
    par = [f.cx, f.cy, sp, o.side === 'right' ? 1 : -1];
  } else if (kind === 'title' || kind === 'bpm') {
    const nText = kind === 'bpm' ? Math.floor(N * 0.62) : N;
    const s = o.text ? sampleInk(o.text, nText, o.seed) : null;
    const slot = kind === 'bpm' ? L.left : L.top;
    if (s) {
      const f = fit(slot, s.w, s.h, 0.94);
      for (let i = 0; i < nText; i++) { const sx = f.cx + s.pts[i * 2] * f.k, sy = f.cy + s.pts[i * 2 + 1] * f.k; set(i, sx, sy, 0, palX(sx), sx, sy); }
    } else {
      for (let i = 0; i < nText; i++) { const sx = slot.x0 + (slot.x1 - slot.x0) * rnd(), sy = slot.y0 + (slot.y1 - slot.y0) * rnd(); set(i, sx, sy, 0, palX(sx), sx, sy); }
    }
    if (kind === 'bpm') {
      // LE MÉTRONOME, à droite : un socle (trapèze) fixe et un bras qui balance
      const R = L.right, f = fit(R, 1, 1.25, 0.9), Hh = 1.25 * f.k, Ww = f.k;
      const pivot = [f.cx, f.cy - Hh * 0.38], arm = Hh * 0.78;
      const m = N - nText, nBase = Math.floor(m * 0.55);
      const base = [[-0.5, -0.5], [0.5, -0.5], [0.22, 0.5], [-0.22, 0.5]], per = 4;
      for (let k = 0; k < nBase; k++) {
        const u = (k + 0.5) / nBase * per, e = Math.floor(u), t = u - e, a = base[e], b = base[(e + 1) % 4];
        const sx = f.cx + (a[0] + (b[0] - a[0]) * t) * Ww * 0.8, sy = f.cy + (a[1] + (b[1] - a[1]) * t) * Hh;
        set(nText + k, sx, sy, 0, [0.9, 0.9, 1.0], sx, sy);
      }
      for (let k = nBase; k < m; k++) {
        // le bras : z = 1 + position le long du bras ; un poids vers 75 %
        const t = k < m - 12 ? (k - nBase + 0.5) / (m - 12 - nBase) : 0.72 + 0.06 * rnd();
        const sx = pivot[0], sy = pivot[1] + t * arm;
        set(nText + k, pivot[0], pivot[1], 1 + t, palX(sx + 0.3), sx, sy);
      }
      par = [arm, 0.45, 0, 0];
    }
  } else if (kind === 'sphere' || kind === 'heart' || kind === 'spiral') {
    const fl = fit(L.left, 1, 1, 0.86), fr = fit(L.right, 1, 1, 0.86);
    const rad = Math.min(fl.k, fr.k) / 2;
    const cy = (fl.cy + fr.cy) / 2;
    par = [fl.cx, cy, fr.cx, rad];
    for (let i = 0; i < N; i++) {
      const right = i >= half, j = right ? i - half : i, n = right ? N - half : half;
      const cx = right ? fr.cx : fl.cx;
      let lx, ly, lz = 0, tx, ty, tz;
      if (kind === 'sphere') {
        // la sphère de Fibonacci
        const yy = 1 - 2 * (j + 0.5) / n, r = Math.sqrt(1 - yy * yy), th = j * 2.399963;
        lx = Math.cos(th) * r; ly = yy; lz = Math.sin(th) * r;
        tx = lx + (right ? 4 : 0); ty = ly; tz = lz;
      } else if (kind === 'heart') {
        // un coeur rempli : rejet sur une grille dispersée, l'équation implicite
        let px, py;
        for (let t = 0; t < 200; t++) { px = (rnd() * 2 - 1) * 1.15; py = (rnd() * 2 - 1) * 1.15; const X = px, Y = py + 0.15; if (Math.pow(X * X + Y * Y - 1, 3) - X * X * Y * Y * Y <= 0) break; }
        lx = px / 1.2; ly = py / 1.2;
        tx = lx + (right ? 4 : 0); ty = ly; tz = 0;
      } else {
        // trois bras de spirale ; x = rayon, y = angle de départ, z = sens
        const arm = j % 3, r = 0.08 + 0.92 * Math.sqrt((Math.floor(j / 3) + 0.5) / Math.ceil(n / 3));
        const th0 = arm * 2.0944 + r * 3.0 * Math.PI;
        lx = Math.cos(th0) * r; ly = Math.sin(th0) * r * 0.92;
        tx = r; ty = th0; tz = right ? 1 : -1;
      }
      const sx = cx + lx * rad, sy = cy + ly * rad;
      set(i, tx, ty, tz, palX(sx + (kind === 'sphere' ? lz * 0.2 : 0)), sx, sy);
    }
  } else if (kind === 'wave') {
    // UNE NAPPE qui entoure la pochette : le ciel entier moins la pochette
    // (et sa marge) ; l'écart cherché pour qu'il y ait assez de points
    // (le haut et le bas retirés de l'amplitude de l'onde au temps : 0,035 x 1,8)
    const x0 = L.centred === false ? L.left.x0 : -L.aspect * 0.92, x1 = L.centred === false ? L.right.x1 : L.aspect * 0.92, y0 = L.yBot + 0.065, y1 = L.yTop - 0.065, c = L.cover;
    const inside = (x, y) => !c.none && Math.abs(x - c.cx) < c.hw + 0.12 && Math.abs(y - c.cy) < c.hh + 0.12;
    const count = (s) => { let k = 0; for (let y = y0; y <= y1 + 1e-6; y += s) for (let x = x0; x <= x1 + 1e-6; x += s) if (!inside(x, y)) k++; return k; };
    let lo = 0.005, hi = 0.5;
    for (let k = 0; k < 30; k++) { const m = (lo + hi) / 2; if (count(m) >= N) lo = m; else hi = m; }
    const pts = [];
    for (let y = y0; y <= y1 + 1e-6; y += lo) for (let x = x0; x <= x1 + 1e-6; x += lo) if (!inside(x, y)) pts.push(x, y);
    const have = pts.length / 2;
    for (let i = 0; i < N; i++) { const j = Math.min(have - 1, Math.floor(i * have / N)); const sx = pts[j * 2], sy = pts[j * 2 + 1]; set(i, sx, sy, sx * 4.0, palX(sx), sx, sy); }
    // l'amplitude reste sous la marge de 0,12 autour de la pochette
    par = [0.035, 4.0, 0, 0];
  }
  return { kind, tex, screen, par };
}

// L'ASSIGNATION : chaque drone (position actuelle prev[d]) reçoit la cible de
// même rang le long d'une courbe de Hilbert. Rend la formation réordonnée.
export function assign(prev, f, N) {
  if (!prev) return f;
  // in coordinates NORMALISED to each formation's own box (15/10/2026): the
  // top left of one goes to the top left of the next, a smooth stretch of
  // the whole - in absolute coordinates the matched neighbours left in all
  // directions and the formation scrambled in place, like a dissolve
  const n = 1024;
  const order = (arr) => {
    let x0 = 1e9, x1 = -1e9, y0 = 1e9, y1 = -1e9;
    for (let i = 0; i < N; i++) { const x = arr[i * 2], y = arr[i * 2 + 1]; if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
    const sx = Math.max(1e-6, x1 - x0), sy = Math.max(1e-6, y1 - y0);
    const qx = (v) => Math.max(0, Math.min(n - 1, Math.floor((v - x0) / sx * (n - 1)))), qy = (v) => Math.max(0, Math.min(n - 1, Math.floor((v - y0) / sy * (n - 1))));
    const idx = new Array(N); for (let i = 0; i < N; i++) idx[i] = i;
    const key = new Float64Array(N); for (let i = 0; i < N; i++) key[i] = hilbertD(n, qx(arr[i * 2]), qy(arr[i * 2 + 1])) + i * 1e-7;
    idx.sort((a, b) => key[a] - key[b]); return idx;
  };
  const od = order(prev), ot = order(f.screen);
  const tex = new Float32Array(N * 4), screen = new Float32Array(N * 2);
  for (let r = 0; r < N; r++) {
    const d = od[r], t = ot[r];
    for (let c = 0; c < 4; c++) tex[d * 4 + c] = f.tex[t * 4 + c];
    screen[d * 2] = f.screen[t * 2]; screen[d * 2 + 1] = f.screen[t * 2 + 1];
  }
  return { kind: f.kind, tex, screen, par: f.par };
}

// LA TRAJECTOIRE d'un drone (copie JS exacte du vertex shader, pour les
// tests) : la vague (un retard selon la distance à l'origine de la vague),
// puis l'arc - une Bézier CUBIQUE dont les deux points de contrôle sont levés
// au-dessus des extrémités. Quand le trajet passe devant la pochette, les deux
// sont levés à une hauteur commune H = 1,5 (haut + 0,1) - 0,25 (yA + yB) :
// toute la partie du trajet au-dessus de la pochette passe plus haut qu'elle
// (une quadratique à un seul point levé passait par-dessus au sommet mais
// coupait les coins de la pochette sur les épaules de l'arc).
export function trajectoire(pA, pB, prog, waveOrigin, cover) {
  const dd = Math.min(1, Math.hypot(pA[0] - waveOrigin[0], pA[1] - waveOrigin[1]) / 3.0);
  const t0 = Math.max(0, Math.min(1, prog * 1.25 - dd * 0.25));
  const t = t0 * t0 * t0 * (t0 * (t0 * 6 - 15) + 10);
  const dist = Math.hypot(pB[0] - pA[0], pB[1] - pA[1]);
  const h = 0.08 + 0.35 * dist;
  let c1 = pA[1] + h, c2 = pB[1] + h;
  const cl = cover.cx - cover.hw - 0.1, cr = cover.cx + cover.hw + 0.1;
  if (!cover.none && Math.max(pA[0], pB[0]) > cl && Math.min(pA[0], pB[0]) < cr) {
    const H = Math.min(1.5, 1.5 * (cover.cy + cover.hh + 0.1) - 0.25 * (pA[1] + pB[1]));
    c1 = Math.max(c1, H); c2 = Math.max(c2, H);
  }
  const u = 1 - t, b0 = u * u * u, b1 = 3 * u * u * t, b2 = 3 * u * t * t, b3 = t * t * t;
  return contourner([b0 * pA[0] + b1 * pA[0] + b2 * pB[0] + b3 * pB[0], b0 * pA[1] + b1 * c1 + b2 * c2 + b3 * pB[1]], cover);
}
// LE CONTOURNEMENT : un champ qui écarte un point de la pochette vers son bord
// le plus proche (horizontalement), plein dedans, nul à 0,08 au-dehors. Un
// drone parti SOUS la pochette (la nappe en a) montait sinon à travers elle.
// Les formations au repos sont toutes à plus de 0,09 : elles ne le sentent pas.
export function contourner(q, cover) {
  if (cover.none) return q;
  const M = 0.08, hw = cover.hw + 0.02;
  const dx = Math.abs(q[0] - cover.cx), dyo = Math.max(Math.abs(q[1] - cover.cy) - cover.hh, 0);
  if (dx > hw + M || dyo > M) return q;
  const sm = (e0, e1, x) => { const k = Math.max(0, Math.min(1, (x - e0) / (e1 - e0))); return k * k * (3 - 2 * k); };
  const w = (1 - sm(0, M, dyo)) * (1 - sm(hw, hw + M, dx));
  const edge = cover.cx + (q[0] >= cover.cx ? 1 : -1) * (hw + 0.01);
  return [q[0] + (edge - q[0]) * w, q[1]];
}

// le texte du worker : les fonctions ci-dessus, puis le gestionnaire de messages
export function formationWorkerSource() {
  const fns = [packColour, hilbertD, fit, rngOf, sampleInk, paletteAt, formation, assign];   // (trajectoire et contourner sont dans le vertex shader)
  return 'var KINDS = ' + JSON.stringify(KINDS) + ';\n' +
    fns.map((f) => 'var ' + f.name + ' = ' + f.toString() + ';').join('\n') + '\n' +
    'self.onmessage = function (ev) {\n' +
    '  var m = ev.data, t0 = performance.now();\n' +
    '  try {\n' +
    '    var f = formation(m.kind, m.o);\n' +
    '    var r = assign(m.prev, f, m.o.N);\n' +
    '    var ms = performance.now() - t0;\n' +
    '    self.postMessage({ id: m.id, kind: m.kind, tex: r.tex, screen: r.screen, par: r.par, ms: ms }, [r.tex.buffer, r.screen.buffer]);\n' +
    '  } catch (e) { self.postMessage({ id: m.id, error: String(e && e.message || e) }); }\n' +
    '};\n';
}
